import { supabase } from '../supabaseClient.js';
// Imported through the utils/ path so the Phase 2 guard recognizes the real
// authenticated helper (it matches the module by source path).
import { authedFetch } from '../utils/backendFetch.js';
import { PAID_PLAN_REQUIRED_MESSAGE } from './rechargeEligibility.js';

// Phase 5/6 credit-wallet RPC surface. Every name and value below is pinned
// to the FROZEN migrations (20260825_credit_paid_connections.sql and
// 20260825_credit_phase5_sql_ops.sql) — the DEF-1/DEF-5 lesson: pin names
// AND values at the call site, never guess. All of these are authenticated
// self-service: the server derives the therapist from auth.uid() only, so
// no user id is ever sent in an RPC argument.

// Copy constant for the per-connection price. The server applies every real
// charge; this exists only so UI copy stays in one place.
export const CREDIT_CONNECTION_PRICE_CREDITS = 10;

// D-014 recharge packs (the pricing design doc is the pricing authority,
// decided 2026-08-19): 20/40/80/160 credits at HK$100/200/400/800, flat
// HK$5/credit. The pack id is the allow-listed string the subscription
// backend accepts; the browser never submits a credit amount and never
// reads a Stripe price id (the server resolves prices from configuration).
export const RECHARGE_PACKS = [
  { packId: '20', credits: 20, amountHkd: 100 },
  { packId: '40', credits: 40, amountHkd: 200 },
  { packId: '80', credits: 80, amountHkd: 400 },
  { packId: '160', credits: 160, amountHkd: 800 },
];

// One operation id (uuid v4) per purchase intent, generated client-side.
// purchase_connection_with_credits is idempotent per (therapist, operation
// id) and raises 22023 when the same id arrives with a different normalized
// email, so a retry of the same intent must REUSE the id while a new intent
// (a different email, or after a success) needs a fresh one.
export const newPurchaseOperationId = () => crypto.randomUUID();

// The therapist's current tier (credit_tier first, plan_name fallback) —
// the same derivation credit_wallet_private.connection_tier_state uses.
// Only selects the target tier for the capacity pre-check; the purchase RPC
// remains authoritative. Failures return null and skip the pre-check.
export async function deriveCurrentTier(userId) {
  if (!userId) return null;
  try {
    const { data, error } = await supabase
      .from('subscription_info')
      .select('plan_name, credit_tier')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error || !data) return null;
    return (data.credit_tier || data.plan_name || '').trim().toLowerCase() || null;
  } catch {
    return null;
  }
}

// check_tier_capacity(p_target_tier) -> {allowed, active_credit_connections,
// capacity, target_tier, arithmetic}. Browser form: no p_user_id, so the
// RPC takes auth.uid(). Called before offering a credit purchase; the
// denial arithmetic is shown verbatim.
export async function checkTierCapacity(targetTier) {
  const { data, error } = await supabase.rpc('check_tier_capacity', {
    p_target_tier: targetTier,
  });
  return { data, error };
}

// purchase_connection_with_credits(p_client_email, p_operation_id) -> jsonb.
// Soft denials return data {purchased: false, reason, ...}; hard failures
// (invalid request 22023, operation-id/email conflict 22023, auth 42501)
// come back as RPC errors.
export async function purchaseConnectionWithCredits(clientEmail, operationId) {
  const { data, error } = await supabase.rpc('purchase_connection_with_credits', {
    p_client_email: clientEmail,
    p_operation_id: operationId,
  });
  return { data, error };
}

// list_therapist_access_codes() -> rows (id, code, client_email,
// mindvault_account_id, status, funding, live, display_status,
// current_period_end, grace_ends_at, over_cap_ends_at, created_at,
// canceled_at). display_status is the authority-derived liveness label;
// liveness is never re-derived in the browser.
export async function listTherapistAccessCodes() {
  const { data, error } = await supabase.rpc('list_therapist_access_codes');
  return { data, error };
}

// cancel_therapist_access_code(p_code) -> {access_code_id, status, funding,
// replayed}. p_code is the ROW id. Credit rows cancel idempotently (a
// replay returns replayed); fiat rows refuse (55000) — the UI must show a
// disabled state for fiat rows instead of calling this.
export async function cancelTherapistAccessCode(codeId) {
  const { data, error } = await supabase.rpc('cancel_therapist_access_code', {
    p_code: codeId,
  });
  return { data, error };
}

// POST /api/recharge-checkout on the subscription backend (auth via the
// authedFetch convention). The body carries ONLY the pack id — never a
// userId/customerId/priceId; the server derives the therapist from the
// verified session and resolves amount/price from the D-014 allow-list.
// The endpoint fails closed with 503 'recharge products not configured'
// when unconfigured. payment_intent.succeeded is the definitive grant
// event, so a success here never means credits were added.
export async function startRechargeCheckout(packId) {
  try {
    const response = await authedFetch(
      `${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/recharge-checkout`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pack: packId }),
      },
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      if (data?.code === 'paid_plan_required') {
        return { data: null, error: PAID_PLAN_REQUIRED_MESSAGE };
      }
      if (data?.code === 'subscription_unavailable') {
        return { data: null, error: 'Could not verify your paid plan. Please try again.' };
      }
      if (response.status === 503) {
        return { data: null, error: 'Credit packs are not available yet — try again later.' };
      }
      if (data?.code === 'invalid_pack') {
        return { data: null, error: 'This credit pack is no longer available.' };
      }
      return { data: null, error: data?.error || 'Could not start the credit purchase.' };
    }
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err.message || 'Could not start the credit purchase.' };
  }
}
